/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { Api } from '../../api';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { gridColumn } from 'utils/gridUtils';
import { GridRowParams } from '@material-ui/data-grid';
import { drawerStore } from 'state/drawerStore';
import { FormInputTypes } from 'utils/constants';
import { toJS } from 'mobx';
import { useProvider } from 'state/providerStore';
import ActionsMenu from 'components/other/ActionsMenu';
import { ListItem, Typography } from '@material-ui/core';

const UserDetailsMyVehicles = observer(() => {
  const makesProvider = useProvider(undefined, undefined, false);
  const modelsProvider = useProvider(undefined, undefined, false);
  const vehiclesProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);

  const { userid } = useParams<{ userid: string }>();

  useEffect(() => {
    vehiclesProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.vehicle.listVehiclesByUser({
        skip: offset,
        take: 20,
        sortBy,
        sortOrder,
        userId: userid,
      }),
    );

    vehiclesProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });

    vehiclesProvider.reloadResource();
  }, [vehiclesProvider]);

  const getVehicleMakesDropdown = () => ({
    name: 'make',
    label: 'Manufacturer',
    type: FormInputTypes.multilineSelect,
    options:
      makesProvider.data?.map((make: string) => ({
        optionName: make,
        topRow: make,
        bottomRow: '',
      })) || [],
  });

  const getVehicleModelsDropdown = () => ({
    name: 'model',
    label: 'Model',
    type: FormInputTypes.multilineSelect,
    options:
      modelsProvider.data?.map((model: string) => ({
        optionName: model,
        topRow: model,
        bottomRow: '',
      })) || [],
  });

  const onClickConfirmAddVehicle = async () => {
    const data = toJS(drawerStore.values);

    const vehiclePayload = {
      userId: userid,
      make: data.make,
      model: data.model,
      maxCapacity: data.max_capacity,
      maxChargingPower: data.max_charging_power,
      range: data.range,
      licensePlate: data.license_plate,
      name: data.name,
      image: data.image,
      isActive: true,
      isDeleted: false,
    };

    await Api.vehicle.adminCreateVehicle(vehiclePayload);
    vehiclesProvider.reloadResource();
    modelsProvider.reloadResource();
  };

  const baseVehicleInputs = [
    getVehicleMakesDropdown(),
    getVehicleModelsDropdown(),
    { name: 'max_capacity', label: 'Battery Capacity (kWh)', type: FormInputTypes.textField },
    {
      name: 'max_charging_power',
      label: 'Max Charging Power (kW)',
      type: FormInputTypes.textField,
    },
    { name: 'range', label: 'Range (km)', type: FormInputTypes.textField },
    { name: 'license_plate', label: 'License Plate', type: FormInputTypes.textField },
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
  ];

  const expandedVehicleInputs = () => [
    getVehicleMakesDropdown(),
    getVehicleModelsDropdown(),
    {
      name: 'max_capacity',
      label: 'Battery Capacity (kWh)',
      type: FormInputTypes.disabledTextField,
    },
    {
      name: 'max_charging_power',
      label: 'Max Charging Power (kW)',
      type: FormInputTypes.disabledTextField,
    },
    {
      name: 'range',
      label: 'Range (km)',
      type: FormInputTypes.disabledTextField,
    },
    { name: 'license_plate', label: 'License Plate', type: FormInputTypes.textField },
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
  ];

  const editVehicleInputs = () => [
    getVehicleMakesDropdown(),
    getVehicleModelsDropdown(),
    {
      name: 'max_capacity',
      label: 'Battery Capacity (kWh)',
      type: FormInputTypes.textField,
    },
    {
      name: 'max_charging_power',
      label: 'Max Charging Power (kW)',
      type: FormInputTypes.textField,
    },
    {
      name: 'range',
      label: 'Range (km)',
      type: FormInputTypes.textField,
    },
    { name: 'license_plate', label: 'License Plate', type: FormInputTypes.textField },
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
  ];

  useEffect(() => {
    const fetchMakes = () => Api.vehicle.listVehicleMakes();
    makesProvider.setRequest(fetchMakes);
    makesProvider.reloadResource();
  }, [makesProvider]);

  useEffect(() => {
    if (drawerStore.values.make) {
      const requestFunctionForModels = () =>
        Api.vehicle.listVehicleModelsByMake(drawerStore.values.make);
      modelsProvider.setRequest(requestFunctionForModels);
      modelsProvider.reloadResource();

      drawerStore.setValues({
        ...drawerStore.values,
        model: undefined,
      });
    }
  }, [drawerStore.values.make, modelsProvider]);

  useEffect(() => {
    if (drawerStore.open) {
      drawerStore.setInputs(baseVehicleInputs);
    }
  }, [makesProvider.data, modelsProvider.data]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (drawerStore.values.make && drawerStore.values.model) {
        try {
          const res = await Api.vehicle.getVehicleDetails(
            drawerStore.values.make,
            drawerStore.values.model,
          );

          const vehicleDetails = res.data?.data;

          if (vehicleDetails) {
            drawerStore.setValues({
              ...drawerStore.values,
              max_capacity: vehicleDetails.maxCapacity,
              max_charging_power: vehicleDetails.maxChargingPower,
              range: vehicleDetails.range,
            });

            drawerStore.setInputs(expandedVehicleInputs());
          }
        } catch (error) {
          console.error('Failed to fetch vehicle details', error);
        }
      }
    };

    fetchDetails();
  }, [drawerStore.values.make, drawerStore.values.model]);

  const onClickAddVehicle = () => {
    drawerStore.openDrawer({
      label: 'Add Vehicle',
      inputs: [],
      clickCallback: onClickConfirmAddVehicle,
      buttonLabel: 'Save',
      showButton: true,
    });

    drawerStore.setInputs(baseVehicleInputs);
  };

  const onClickDeleteVehicle = async (vehicle) => {
    try {
      await Api.vehicle.adminDeleteVehicle(vehicle.id);
      vehiclesProvider.reloadResource();
    } catch (error: any) {
      console.error('Error deleting vehicle:', error);
    }
  };

  const onClickConfirmEditVehicle = async (vehicleId: string) => {
    const data = toJS(drawerStore.values);

    const vehiclePayload = {
      manufacturer: data.make,
      model: data.model,
      maxCapacity: data.max_capacity,
      maxChargingPower: data.max_charging_power,
      range: data.range,
      licensePlate: data.license_plate,
      name: data.name,
      image: data.image,
    };

    await Api.vehicle.adminUpdateVehicle(vehicleId, vehiclePayload);
    vehiclesProvider.reloadResource();
    drawerStore.reset();
  };

  const onClickEditVehicle = (vehicle) => {
    drawerStore.openDrawer({
      label: 'Edit Vehicle',
      inputs: [],
      clickCallback: () => onClickConfirmEditVehicle(vehicle.id),
      buttonLabel: 'Save',
      showButton: true,
    });

    drawerStore.setValues({
      make: vehicle.manufacturer,
      model: vehicle.model,
      max_capacity: vehicle.maxCapacity,
      max_charging_power: vehicle.maxChargingPower,
      range: vehicle.range,
      license_plate: vehicle.licensePlate,
      name: vehicle.name,
      image: vehicle.image,
    });

    drawerStore.setInputs(editVehicleInputs());
  };

  const vehicleActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => onClickEditVehicle(params.row)}>
        <Typography style={{ width: '100%' }}>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => onClickDeleteVehicle(params.row)}>
        <Typography style={{ width: '100%' }}>Delete</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const myVehiclesColumns = [
    gridColumn('name', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params.row.name}</div>,
    }),
    gridColumn('License Plate', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params.row.licensePlate}</div>,
    }),
    gridColumn('manufacturer', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params.row.manufacturer}</div>,
    }),
    gridColumn('model', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params.row.model}</div>,
    }),
    gridColumn('Battery Capacity', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div>{params.row.maxCapacity ? `${params.row.maxCapacity} kWh` : '-'}</div>
      ),
    }),
    gridColumn('Max Power', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div>{params.row.maxChargingPower ? `${params.row.maxChargingPower} kW` : '-'}</div>
      ),
    }),
    gridColumn('Range', {
      flex: 2,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params.row.range}</div>,
    }),
    gridColumn('', {
      width: 60,
      renderCell: (params: GridRowParams) => vehicleActions(params),
      align: 'center',
      headerAlign: 'center',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        label="Vehicles"
        rows={vehiclesProvider.data}
        onClickAdd={onClickAddVehicle}
        columns={myVehiclesColumns}
        onPageOffsetChange={vehiclesProvider.onPageChange}
        onSortTypeChange={vehiclesProvider.setSort}
      />
    </div>
  );
});

export default UserDetailsMyVehicles;
